import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import PanelShell from "../../shared/PanelShell";
import { SkeletonChart } from "../../shared/Skeleton";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";

// TREND — Tier 2, under the money strip (V6 §E.3). Plots `conversations` and
// `bookings` over the period; pct_abandoned is deliberately NOT plotted (three
// series at this width is unreadable). Lifted verbatim from the v1 MoneyTier's
// inline chart so it survives that container's teardown.
//
// `series[].date` is the x value; `trend.bucket` is the GRANULARITY ("day"/"week"),
// NOT a field name — using it as a dataKey resolves to undefined on every point and
// takes the axis domain down with it (verified against the live payload). Read the
// field, never assume the bucket.
const TrendChart = ({ panelKey = "voice_trend", say, trend, loading = false }) => {
  const series = Array.isArray(trend?.series) ? trend.series : [];
  return (
    <PanelShell
      panelKey={panelKey}
      label="Conversations & bookings over time"
      say={say}
      loading={loading}
      hasData={series.length > 0}
      fullWidth
    >
      {loading && !trend ? (
        <SkeletonChart height={240} />
      ) : (
        <div>
          <div className="h-[240px] w-full" data-testid="trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`trendConvos_${panelKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#32B1FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#32B1FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-800" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-gray-500" />
              <YAxis tick={{ fontSize: 11 }} className="fill-gray-500" />
              <ThemedChartTooltip />
              <Area type="monotone" dataKey="conversations" name="Conversations" stroke="#32B1FF" fill={`url(#trendConvos_${panelKey})`} />
              <Area type="monotone" dataKey="bookings" name="Bookings" stroke="#059669" fillOpacity={0} />
            </AreaChart>
          </ResponsiveContainer>
          </div>
          {/* Legend — below the chart. Both series read as a line swatch; in the
              chart itself they still differ by fill (Conversations is a filled area,
              Bookings a bare line), which carries the distinction in greyscale (Markup #7). */}
          <div className="mt-2 flex items-center justify-center gap-4 px-1" data-testid="trend-legend">
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <span className="inline-block h-[3px] w-4 rounded-full bg-[#32B1FF]" aria-hidden="true" />
              Conversations
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <span className="inline-block h-[3px] w-4 rounded-full bg-[#059669]" aria-hidden="true" />
              Bookings
            </span>
          </div>
        </div>
      )}
    </PanelShell>
  );
};

export default TrendChart;
